import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, useHistory } from 'react-router-dom';
import { isEmpty } from 'lodash-es';
import { Tooltip } from 'component-library-and-tools';
import Button from 'shared/components/Button';
import LoadingWrapper from 'shared/components/LoadingWrapper';
import { RouteWithSubRoutes } from 'shared/components/route';
import { CREATE_WORKFLOW_PATH } from 'shared/constants/routePaths';
import { Workflow } from 'shared/types/workflow';
import { FETCH_WORKFLOWS, CREATE_WORKFLOW } from 'services/workflow/actions';
import { fetchWorkflows } from 'services/workflow/actionCreators';
import { fetchSafelistSummaries, FETCH_SAFELIST_SUMMARIES } from 'services/safelists/actions';
import { getSafelistSummaryMap } from 'services/safelists/getters';
import { getOrderedWorkflowList } from 'services/workflow/getters';
import { canUserManageWorkflows } from 'services/user/getters';
import { IndexableObjectType } from 'shared/types/common';

import { RootState } from '../../../index';

import WorkflowCreatedModal from '../../workflow-creation/WorkflowCreatedModal';

import FilterBar from '../FilterBar';
import WorkflowList from '../WorkflowList';

import { WorkflowListViewProps } from './types';

const WORKFLOWS_LIMIT = 5

const StyledLoadingWrapper = styled(LoadingWrapper)`
  flex: 1;
  overflow-y: auto;
`;

// TODO: Instead of applying it in here, we should normalize the `line-height` in a "base" CSS file.
const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1;
  min-height: 100%;
  max-height: 100%;
`;

const StyledHeader = styled.div`
  margin: 2.333333333rem 2.5rem 0;
`;

const StyledTitle = styled.h1`
  margin: 0 0 3.666666667rem;
  font-size: 2.333333333rem;
`;

const StyledFilterContainer = styled.div`
  display: flex;
  height: 3.666666667rem;
`;

const StyledFilterBar = styled(FilterBar)`
  flex: 1;
  margin-right: 2rem;
`;

const StyledContent = styled.div`
  flex: 1;
  height: 100%;
  box-sizing: border-box;
  padding: 1.333333333rem 2.5rem 0;
  overflow-y: auto;
`;

const StyledNoWorkflowsMessageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledNoWorkflowsMessage = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  color: #bbb;
  font-size: 2.5rem;
  font-weight: 700;
`;

const WorkflowListView = ({ routes }: WorkflowListViewProps) => {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState<IndexableObjectType>();
  const [workflowsCount, setWorkflowsCount] = useState(0);
  const workflows = useSelector<RootState, Workflow[]>(getOrderedWorkflowList);
  const safelistSummaries = useSelector<RootState, IndexableObjectType>(getSafelistSummaryMap);
  const canManageWorkflows = useSelector(canUserManageWorkflows);
  const history = useHistory();

  const handleFilterChange = useCallback((filters) => {
    setFilters(filters)
    dispatch(fetchWorkflows(filters));
  }, []);

  const handleButtonClick = useCallback(() => {
    history.push(CREATE_WORKFLOW_PATH);
  }, []);

  useEffect(() => {
    if (isEmpty(workflows)) {
      dispatch(fetchWorkflows());
    }

    if (isEmpty(safelistSummaries)) {
      dispatch(fetchSafelistSummaries());
    }
  }, []);

  useEffect(() => {
    if (isEmpty(filters) || filters?.name == '') {
      setWorkflowsCount(workflows?.length || 0);
    }
  }, [workflows]);

  return (
    <StyledContainer>
      <StyledHeader>
        <StyledTitle>Workflows</StyledTitle>
        <StyledFilterContainer>
          <StyledFilterBar onFilter={handleFilterChange} />
          {canManageWorkflows && (
            <Tooltip content={workflowsCount>=WORKFLOWS_LIMIT?`Cannot create more than ${WORKFLOWS_LIMIT} workflows`: 'Create Workflow'}>
              <Button
                label='Create Workflow'
                onClick={handleButtonClick}
                isDisabled={workflowsCount >= WORKFLOWS_LIMIT}
              />
            </Tooltip>
          )}
        </StyledFilterContainer>
      </StyledHeader>
      <StyledLoadingWrapper actionToQuery={[
        FETCH_WORKFLOWS,
        CREATE_WORKFLOW,
        FETCH_SAFELIST_SUMMARIES,
      ]}>
        {workflows.length
          ? <StyledContent>
              <WorkflowList workflows={workflows} />
            </StyledContent>
          : <StyledNoWorkflowsMessageContainer>
              <StyledNoWorkflowsMessage>
                There are no workflows available.
              </StyledNoWorkflowsMessage>
            </StyledNoWorkflowsMessageContainer>
        }
      </StyledLoadingWrapper>
      <Switch>
        {(routes||[]).map((route) => (
          <RouteWithSubRoutes
            key={route.name}
            {...route}
          />
        ))}
      </Switch>
      {/* Modals */}
      <WorkflowCreatedModal />
    </StyledContainer>
  );
};

export default WorkflowListView;
